import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { PLANES_PUBLICOS } from '../lib/planesPublicos';

/** Cada cuánto se relee el plan del usuario desde la base de datos. */
const PLAN_REFRESH_INTERVAL_MS = 60_000;

interface UserPlanRow {
  id?: string;
  trial_end: string | null;
  plan_id: string | null;
  plan_active: boolean | null;
  plan_ends_at: string | null;
  created_at: string | null;
}

interface Plan {
  id: string;
  name: string;
  price: number;
  features: string[];
  active: boolean;
  color?: string;
  icon?: string;
}

interface TrialInfo {
  isActive: boolean;
  daysLeft: number;
  hoursLeft: number;
  minutesLeft: number;
  startDate: Date;
  endDate: Date;
  hasExpired: boolean;
}

async function postWebnotiPlanPurchaseEvent() {
  try {
    const { data } = await supabase.auth.getSession();
    const accessToken = data?.session?.access_token;
    if (!accessToken) return;

    await fetch('/api/webnoti/event', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        access_token: accessToken,
        event: 'plan_purchase',
        target: 'user',
      }),
    });
  } catch {
    // ignore
  }
}

async function postPlanInvoiceEmail(payload: {
  planId: string;
  planName: string;
  billingPeriod: 'monthly' | 'annual';
  amount: number;
}) {
  try {
    const { data } = await supabase.auth.getSession();
    const accessToken = data?.session?.access_token;
    if (!accessToken) return;

    await fetch('/api/plans/send-plan-invoice', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        access_token: accessToken,
        plan_id: payload.planId,
        plan_name: payload.planName,
        billing_period: payload.billingPeriod,
        amount: payload.amount,
      }),
    });
  } catch {
    // ignore
  }
}

const PLAN_NAMES: Record<string, string> = {
  'pyme': 'PYME',
  'pro': 'PRO',
  'plus': 'PLUS',
  'facturacion-150': 'Plan 150 Facturas al mes',
  'facturacion-200': 'Plan 200 Facturas',
  'facturacion-300': 'Plan 300 Facturas',
  'facturacion-500': 'Plan 500 Facturas',
  'facturacion-1000': 'Plan 1000 Facturas',
  'facturacion-simple': 'Plan 2500 Facturas',
  'facturacion-premium': 'Plan 3500 Facturas',
  'facturacion-prepago-75': 'Plan 75 Facturas',
  'facturacion-prepago-150': 'Plan 150 Facturas',
  'facturacion-prepago-350': 'Plan 350 Facturas',
  'facturacion-prepago-750': 'Plan 750 Facturas',
  'pos-premium': 'POS Premium',
};

const PLAN_PRICES: Record<string, number> = {
  'pyme': 1149.99,
  'pro': 2549.99,
  'plus': 13000,
  'facturacion-150': 849.99,
  'facturacion-200': 1149.99,
  'facturacion-300': 1800,
  'facturacion-500': 2149.99,
  'facturacion-1000': 4500,
  'facturacion-simple': 5800,
  'facturacion-premium': 6000,
  // El precio de verdad vive en `public.plan_limits.price_cents`, que es de donde lo lee el
  // cobro; esto es la copia con la que se redacta la factura que se manda por correo. Si las
  // dos se separan, el cliente paga una cosa y recibe el comprobante por otra — que es
  // exactamente lo que pasó con los prepago antes de la migración 016.
  'facturacion-prepago-75': 749.99,
  'facturacion-prepago-150': 1149.99,
  'facturacion-prepago-350': 1849.99,
  'facturacion-prepago-750': 3500,
  'pos-premium': 8400,
};

// La ficha del plan activo usa el mismo catálogo que las tarjetas públicas para que lo que el
// cliente compra y lo que luego ve en su perfil nunca describan beneficios distintos.
const PLAN_FEATURES = Object.fromEntries(
  PLANES_PUBLICOS.map((plan) => [plan.id, plan.features]),
) as Record<string, string[]>;

const PLAN_DISPLAY: Record<string, { name: string; color: string; icon: string }> = {
  'pyme': { name: 'PYME', color: 'from-blue-500 to-blue-600', icon: 'ri-building-2-line' },
  'pro': { name: 'PRO', color: 'from-indigo-500 to-indigo-600', icon: 'ri-rocket-line' },
  'plus': { name: 'PLUS', color: 'from-purple-500 to-purple-600', icon: 'ri-vip-crown-line' },
  'facturacion-prepago-75': { name: 'Plan 75 Facturas', color: 'from-emerald-500 to-emerald-600', icon: 'ri-bill-line' },
  'facturacion-prepago-150': { name: 'Plan 150 Facturas', color: 'from-emerald-500 to-emerald-600', icon: 'ri-bill-line' },
  'facturacion-prepago-350': { name: 'Plan 350 Facturas', color: 'from-emerald-500 to-emerald-600', icon: 'ri-bill-line' },
  'facturacion-prepago-750': { name: 'Plan 750 Facturas', color: 'from-emerald-500 to-emerald-600', icon: 'ri-bill-line' },
};

function getPlanName(planId: string): string {
  return PLAN_NAMES[planId] || planId;
}

function getPlanPrice(planId: string): number {
  return PLAN_PRICES[planId] || 0;
}

function getPlanFeatures(planId: string): string[] {
  return PLAN_FEATURES[planId] || [];
}

function getDisplayInfo(planId: string) {
  return PLAN_DISPLAY[planId] || {
    name: getPlanName(planId),
    color: 'from-gray-500 to-gray-600',
    icon: 'ri-question-line',
  };
}

function calculateTimeLeft(endDate: Date) {
  const now = new Date();
  const diff = endDate.getTime() - now.getTime();

  if (diff <= 0) {
    return { daysLeft: 0, hoursLeft: 0, minutesLeft: 0, isActive: false, hasExpired: true };
  }

  return {
    daysLeft: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hoursLeft: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutesLeft: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    isActive: true,
    hasExpired: false,
  };
}

function isTrialUsed(): boolean {
  return localStorage.getItem('contard_trial_used') === 'true';
}

function parseUserRow(data: UserPlanRow) {
  const createdAt = data.created_at ? new Date(data.created_at) : null;
  const explicitTrialEnd = data.trial_end ? new Date(data.trial_end) : null;
  const trialEnd = explicitTrialEnd || (createdAt ? new Date(createdAt.getTime() + 7 * 24 * 60 * 60 * 1000) : null);
  const planEndsAt = data.plan_ends_at ? new Date(data.plan_ends_at) : null;
  const planId = data.plan_id ?? '';
  const planActiveFlag = data.plan_active === true;

  // Resuelve inconsistencia: si plan_ends_at ya venció, el plan NO está activo
  // aunque plan_active = true.
  const planIsActive = planActiveFlag && (!planEndsAt || planEndsAt.getTime() > Date.now());

  return { createdAt, explicitTrialEnd, trialEnd, planEndsAt, planId, planActiveFlag, planIsActive };
}

export function usePlans() {
  const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
  const [trialInfo, setTrialInfo] = useState<TrialInfo>({
    isActive: true,
    daysLeft: 7,
    hoursLeft: 0,
    minutesLeft: 0,
    startDate: new Date(),
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    hasExpired: false,
  });

  // ---------------------------------------------------------------------------
  // 1. Carga inicial + suscripción Realtime a public.users
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const applyLocalFallback = () => {
      const savedPlan = localStorage.getItem('contard_current_plan');
      if (savedPlan) {
        try {
          const plan = JSON.parse(savedPlan) as Plan;
          if (!cancelled) setCurrentPlan(plan);
        } catch {
          localStorage.removeItem('contard_current_plan');
        }
      }

      const savedTrialInfo = localStorage.getItem('contard_trial_info');
      if (savedTrialInfo) {
        try {
          const parsed = JSON.parse(savedTrialInfo);
          const endDate = new Date(parsed.endDate);
          const startDate = new Date(parsed.startDate);
          if (isNaN(endDate.getTime()) || isNaN(startDate.getTime())) throw new Error('Invalid dates');
          const timeLeft = calculateTimeLeft(endDate);
          if (!cancelled) setTrialInfo({ startDate, endDate, ...timeLeft });
        } catch {
          // ignore
        }
      }
    };

    const loadFromDb = async () => {
      try {
        const { data: session } = await supabase.auth.getSession();
        const uid = session?.session?.user?.id;
        if (!uid) {
          applyLocalFallback();
          return;
        }

        const { data, error } = await supabase
          .from('users')
          .select('trial_end, plan_id, plan_active, plan_ends_at, created_at')
          .eq('id', uid)
          .maybeSingle();

        if (error || !data) {
          applyLocalFallback();
          return;
        }

        const { trialEnd, planEndsAt, planId, planIsActive, explicitTrialEnd } = parseUserRow(data);

        if (!cancelled) {
          if (planId) {
            const info = getDisplayInfo(planId);
            setCurrentPlan({
              id: planId,
              name: info.name,
              price: getPlanPrice(planId),
              features: getPlanFeatures(planId),
              active: planIsActive,
              color: info.color,
              icon: info.icon,
            });
          } else {
            setCurrentPlan(null);
          }

          const trialStartDate = trialEnd ? new Date(trialEnd.getTime() - 7 * 24 * 60 * 60 * 1000) : new Date();
          const endDate = trialEnd ?? new Date(0);
          const timeLeft = trialEnd
            ? calculateTimeLeft(endDate)
            : { daysLeft: 0, hoursLeft: 0, minutesLeft: 0, isActive: false, hasExpired: true };

          setTrialInfo({ startDate: trialStartDate, endDate, ...timeLeft });
        }

        // Persistir trial_end si no existía (created_at + 7d)
        if (!explicitTrialEnd && trialEnd) {
          await supabase.from('users').update({ trial_end: trialEnd.toISOString() }).eq('id', uid);
        }

        if (trialEnd) {
          localStorage.setItem('contard_trial_used', 'true');
          localStorage.setItem(
            'contard_trial_info',
            JSON.stringify({
              startDate: new Date(trialEnd.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
              endDate: trialEnd.toISOString(),
            }),
          );
        }

        if (planId) {
          localStorage.setItem(
            'contard_current_plan',
            JSON.stringify({
              id: planId,
              name: getPlanName(planId),
              price: getPlanPrice(planId),
              features: getPlanFeatures(planId),
              active: planIsActive,
            }),
          );
        } else {
          localStorage.removeItem('contard_current_plan');
        }
      } catch {
        applyLocalFallback();
      }
    };

    // Carga inicial
    loadFromDb();

    // Antes esto era una suscripción Realtime a public.users. RDS no tiene el
    // servidor de replicación de Supabase, así que el plan se vuelve a consultar
    // cada minuto: un cambio de plan hecho desde el panel de administración se
    // refleja en la sesión del usuario en ese margen.
    const interval = setInterval(() => {
      if (!cancelled) void loadFromDb();
    }, PLAN_REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // 2. Timer de actualización cada minuto
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const updateTimer = () => {
      if (trialInfo.endDate && !currentPlan?.active) {
        const timeLeft = calculateTimeLeft(trialInfo.endDate);
        setTrialInfo((prev) => ({ ...prev, ...timeLeft }));
        if (timeLeft.hasExpired) {
          localStorage.setItem('contard_trial_expired', 'true');
        }
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000);
    return () => clearInterval(interval);
  }, [trialInfo.endDate, currentPlan?.active]);

  // ---------------------------------------------------------------------------
  // 3. Suscribir a un plan
  // ---------------------------------------------------------------------------
  const subscribeToPlan = async (
    planId: string,
    options?: { billingPeriod?: 'monthly' | 'annual'; amount?: number; planName?: string },
  ) => {
    try {
      const now = new Date();

      // Los prepago valen un año, no un mes: es lo que anuncia la web («Vigencia de un año») y
      // lo que cobra su precio. Poniéndoles 30 días como a los demás caducaban once meses antes
      // de tiempo, y de paso la bolsa de facturas se reabría cada mes.
      const isPrepaid = planId.startsWith('facturacion-prepago-');
      const days = isPrepaid || options?.billingPeriod === 'annual' ? 365 : 30;
      const endsAt = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);

      const { data: session } = await supabase.auth.getSession();
      const uid = session?.session?.user?.id;
      if (uid) {
        const { error } = await supabase
          .from('users')
          .update({
            plan_id: planId,
            plan_active: true,
            plan_ends_at: endsAt.toISOString(),
            // Marca el arranque del ciclo. De aquí cuelga el conteo de facturas del plan: sin
            // esto, una renovación no reiniciaría el cupo. Ver migración 014.
            plan_started_at: now.toISOString(),
          })
          .eq('id', uid);
        if (error) throw error;
      }

      const info = getDisplayInfo(planId);
      const plan: Plan = {
        id: planId,
        name: info.name,
        price: getPlanPrice(planId),
        features: getPlanFeatures(planId),
        active: true,
        color: info.color,
        icon: info.icon,
      };

      setCurrentPlan(plan);
      setTrialInfo((prev) => ({ ...prev, isActive: false, hasExpired: false }));
      localStorage.setItem('contard_current_plan', JSON.stringify(plan));
      localStorage.removeItem('contard_trial_expired');

      await postWebnotiPlanPurchaseEvent();

      const billingPeriod = options?.billingPeriod || 'monthly';
      const amount = Number(options?.amount ?? getPlanPrice(planId)) || 0;
      const planName = (options?.planName || plan.name || getPlanName(planId)).trim();

      if (amount > 0 && planId !== 'student') {
        await postPlanInvoiceEmail({ planId, planName, billingPeriod, amount });
      }

      return { success: true };
    } catch (error) {
      console.error('Error subscribing to plan:', error);
      return { success: false, error: 'Error al procesar la suscripción' };
    }
  };

  // ---------------------------------------------------------------------------
  // 4. Cancelar suscripción
  // ---------------------------------------------------------------------------
  const cancelSubscription = async () => {
    setCurrentPlan(null);
    localStorage.removeItem('contard_current_plan');
    setTrialInfo((prev) => ({
      ...prev,
      isActive: false,
      hasExpired: true,
      daysLeft: 0,
      hoursLeft: 0,
      minutesLeft: 0,
    }));
    localStorage.setItem('contard_trial_expired', 'true');
    return { success: true };
  };

  // ---------------------------------------------------------------------------
  // 5. Helpers de acceso
  // ---------------------------------------------------------------------------
  const hasAccess = () => {
    return (currentPlan?.active === true) || (trialInfo.isActive && !trialInfo.hasExpired);
  };

  const canSelectSpecificPlan = (planId?: string) => {
    if (!planId) return true;
    if (currentPlan?.active === true) {
      return currentPlan.id.toLowerCase() !== planId.toLowerCase();
    }
    return true;
  };

  const getTrialStatus = (): 'subscribed' | 'expired' | 'warning' | 'active' => {
    if (currentPlan?.active) return 'subscribed';
    if (trialInfo.hasExpired) return 'expired';
    if (trialInfo.daysLeft <= 3) return 'warning';
    return 'active';
  };

  const startTrialWithPlan = (planId: string) => {
    if (isTrialUsed()) {
      return { success: false, error: 'Ya has utilizado tu período de prueba gratuito' };
    }

    const startDate = new Date();
    const endDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    const newTrialInfo: TrialInfo = {
      isActive: true,
      daysLeft: 7,
      hoursLeft: 0,
      minutesLeft: 0,
      startDate,
      endDate,
      hasExpired: false,
    };

    localStorage.setItem(
      'contard_trial_info',
      JSON.stringify({ startDate: startDate.toISOString(), endDate: endDate.toISOString() }),
    );
    localStorage.setItem('contard_trial_used', 'true');
    localStorage.setItem('contard_trial_plan', planId);
    setTrialInfo(newTrialInfo);
    return { success: true };
  };

  return {
    currentPlan,
    trialInfo,
    subscribeToPlan,
    cancelSubscription,
    hasAccess,
    canSelectPlan: canSelectSpecificPlan,
    getTrialStatus,
    hasUsedTrial: isTrialUsed,
    startTrialWithPlan,
  };
}
